import {produce} from 'immer';
import {useCallback, useMemo} from 'react';

import {assert} from 'Asserts';
import DocumentActions from 'notebook/documents/DocumentActions';
import DocumentStore from 'notebook/documents/DocumentStore';
import DocumentUtil from 'notebook/documents/DocumentUtil';
import {ReorderStepEvent} from 'procedures/design/method-config/MethodEditor';
import {PROCEDURE_PRODUCT_LIMITS} from 'procedures/design/ProcedureDesignConstants';
import {ADDING_STEP_TEXT} from 'procedures/design/step-config/StepConfigConstants';
import {StepGroupUpdater} from 'procedures/design/step-config/StepConfigEditor';
import {
  addStepDirectlyToStepGroup,
  copyStepWithPersistenceInfo,
  findStep,
  getNextStepCidAfterRemove,
  getUpdatedStepGroupsWithCopiedStep,
  makeEmptyStepGroups,
  moveStepArbitrarily as moveStepArbitrarilyUtil,
} from 'procedures/design/step-config/stepManagementUtils';
import {Method, ProcedureFlowchart, ProcedureStepGroup} from 'procedures/ProcedureDomainTypes';
import {
  BlockingSaveObjectSource,
  BlockingSaveOperation,
  PersistenceInfo,
  ProcedurePageContext,
} from 'procedures/ProcedurePageContext';
import MethodSidepanelContext from 'procedures/processes/MethodSidepanelContext';
import runConfirmDeleteModal from 'ui-library/modals/runConfirmDeleteModal';

export interface UseStepOperationsParams {
  method: Method;
  onChangeSteps: (stepGroupUpdater: StepGroupUpdater) => void;
  onChangeSelection: (newStepId: string) => void;
  onReorderedStep: (params: ReorderStepEvent) => void;
  locked: boolean;
}

export interface StepOperations {
  moveStep: (stepGroupIndex: number, stepIndex: number, direction: number) => void;
  moveStepArbitrarily: (stepCidToMove: string, targetStepCid: string) => void;
  addStep: (stepGroupIndex: number, stepIndex: number, stepName?: string, fromId?: string) => Promise<void>;
  copyStep: (stepGroupIndex: number, stepIndex: number) => Promise<void>;
  deleteStepWithConfirmation: (stepCid: string) => Promise<void>;
  isStepLimitReached: boolean;
}

interface StepSaveDeps {
  powSaveImmediately: ((updater: (method: Method) => Method) => Promise<void>) | null;
  legacySaveImmediately: (
    applyChanges: (fc: ProcedureFlowchart) => PersistenceInfo,
    overlayMessage: string
  ) => Promise<unknown>;
  onChangeSteps: (stepGroupUpdater: StepGroupUpdater) => void;
  onChangeSelection: (newStepId: string) => void;
}

/**
 * Add a new step to the specified step group, saving immediately via the appropriate save path.
 */
export async function performAddStep(
  stepGroupIndex: number,
  stepIndex: number,
  deps: StepSaveDeps,
  stepName?: string,
  fromId?: string
): Promise<void> {
  const {powSaveImmediately, legacySaveImmediately, onChangeSteps, onChangeSelection} = deps;

  if (powSaveImmediately) {
    // TODO(BNCH-132164): The document associated with the new step will not be visible until the user
    //   does a hard refresh. It will just be a spinner, since the CIDs returned from the save call are
    //   not merged into the procedure (see ProcedureRootNodeAdapter.onSaveComplete)
    await powSaveImmediately((currentMethod) => {
      const {updatedStepGroups, newStepCid} = addStepDirectlyToStepGroup(
        currentMethod.stepGroups,
        stepGroupIndex,
        stepIndex,
        stepName,
        fromId
      );
      onChangeSelection(newStepCid);
      return {...currentMethod, stepGroups: updatedStepGroups};
    });
  } else {
    await legacySaveImmediately((_latestFlowchart: ProcedureFlowchart) => {
      onChangeSteps((sg) => {
        const {updatedStepGroups, newStepCid} = addStepDirectlyToStepGroup(
          sg,
          stepGroupIndex,
          stepIndex,
          stepName,
          fromId
        );
        onChangeSelection(newStepCid);
        return updatedStepGroups;
      });
      return {
        blockingSaveObjectSource: BlockingSaveObjectSource.Step,
        blockingSaveOperation: BlockingSaveOperation.Create,
      };
    }, ADDING_STEP_TEXT);
  }
}

/**
 * Duplicate a step, saving immediately via the appropriate save path.
 */
export async function performCopyStep(
  stepGroupIndex: number,
  stepIndex: number,
  stepGroups: Array<ProcedureStepGroup>,
  deps: StepSaveDeps
): Promise<void> {
  const {powSaveImmediately, legacySaveImmediately, onChangeSteps, onChangeSelection} = deps;

  if (powSaveImmediately) {
    // TODO(BNCH-132164): See comments in performAddStep about new documents not being visible until hard refresh
    await powSaveImmediately((currentMethod) => {
      const {newStep} = copyStepWithPersistenceInfo(
        currentMethod.stepGroups,
        stepGroupIndex,
        stepIndex,
        true
      );
      const updatedStepGroups = getUpdatedStepGroupsWithCopiedStep(
        currentMethod.stepGroups,
        stepGroupIndex,
        stepIndex,
        newStep
      );
      onChangeSelection(newStep.cid);
      return {...currentMethod, stepGroups: updatedStepGroups};
    });
  } else {
    await legacySaveImmediately((_latestFlowchart: ProcedureFlowchart) => {
      // Copy the step and prepare the persistence info to return to the saveImmediately call. This
      // does not update the step groups yet, because onChangeSteps will do that. It needs to happen
      // onChangeSteps callback is executed in order to avoid race conditions or overwriting any changes
      // to the step groups.
      const {newStep, persistenceInfo} = copyStepWithPersistenceInfo(stepGroups, stepGroupIndex, stepIndex);

      onChangeSteps((latestStepGroups) => {
        const updatedStepGroups = getUpdatedStepGroupsWithCopiedStep(
          latestStepGroups,
          stepGroupIndex,
          stepIndex,
          newStep
        );
        onChangeSelection(newStep.cid);
        return updatedStepGroups;
      });

      return persistenceInfo;
    }, 'Copying step...');
  }
}

/**
 * Remove a step from step groups by cid.
 * Returns the new step groups, the removed step's documentId (if any), and the next step cid to select.
 * Returns null if the step was not found.
 */
export function removeStepFromGroups(
  stepGroups: Array<ProcedureStepGroup>,
  stepCid: string
): {
  newStepGroups: Array<ProcedureStepGroup>;
  removedDocumentId: string | null;
  nextStepCid: string | null;
} | null {
  let stepGroupIndex = -1;
  let stepIndex = -1;
  for (let i = 0; i < stepGroups.length; i++) {
    const indexInGroup = stepGroups[i].steps.findIndex((s) => s.cid === stepCid);
    if (indexInGroup !== -1) {
      stepGroupIndex = i;
      stepIndex = indexInGroup;
      break;
    }
  }
  if (stepGroupIndex === -1 || stepIndex === -1) {
    return null;
  }

  const removedStep = stepGroups[stepGroupIndex].steps[stepIndex];
  const newStepGroups = produce(stepGroups, (draftStepGroups) => {
    draftStepGroups[stepGroupIndex].steps.splice(stepIndex, 1);
  });
  const nextStepCid =
    getNextStepCidAfterRemove(newStepGroups[stepGroupIndex].steps, newStepGroups, stepIndex) ?? null;

  return {
    newStepGroups,
    removedDocumentId: removedStep.documentId,
    nextStepCid,
  };
}

/**
 * Manages all step CRUD operations for the StepSelectorSidebar: adding, duplicating,
 * deleting, and reordering steps.
 *
 * Consumes ProcedurePageContext and MethodSidepanelContext directly for save operations.
 */
export default function useStepOperations(params: UseStepOperationsParams): StepOperations {
  const {method, onChangeSteps, onChangeSelection, onReorderedStep, locked} = params;
  const {procedureMetadata, legacySaveImmediately} = ProcedurePageContext.useContext();
  const procedureType = procedureMetadata.procedureType;
  const {saveImmediately: powSaveImmediately} = MethodSidepanelContext.useContext();

  const stepGroups = method.stepGroups ?? makeEmptyStepGroups(procedureType);

  const isStepLimitReached =
    stepGroups
      .map((stepGroup) => stepGroup.steps.length)
      .reduce((previousValue, currentValue) => previousValue + currentValue, 0) >=
    PROCEDURE_PRODUCT_LIMITS.STEPS_PER_METHOD;

  /**
   Method to move a step in the given number of direction indexes
   */
  const moveStep = useCallback(
    (stepGroupIndex: number, stepIndex: number, direction: number) => {
      onChangeSteps((sg) =>
        produce(sg, (draftStepGroups) => {
          const {steps} = draftStepGroups[stepGroupIndex];
          const stepToMove = steps[stepIndex];
          steps.splice(stepIndex, 1);
          steps.splice(stepIndex + direction, 0, stepToMove);
        })
      );
      onReorderedStep({
        stepGroupType: stepGroups[stepGroupIndex].group,
        step: stepGroups[stepGroupIndex].steps[stepIndex],
        direction,
      });
    },
    [onReorderedStep, onChangeSteps, stepGroups]
  );

  const moveStepArbitrarily = useCallback(
    (stepCidToMove: string, targetStepCid: string) => {
      assert(!locked, 'Should not be able to drag step for locked procedure');

      onChangeSteps((sg) => {
        const result = moveStepArbitrarilyUtil(sg, stepCidToMove, targetStepCid);
        if (result != null) {
          onReorderedStep({
            step: result.stepBeingMoved,
            stepGroupType: result.stepGroupToMoveFrom,
            direction: 'drag',
          });
          return result.newStepGroups;
        }
        return sg; // No change
      });
    },
    [onChangeSteps, onReorderedStep, locked]
  );

  /**
   Method to delete the step with the given cid
   */
  const deleteStep = useCallback(
    (stepCid: string) => {
      onChangeSteps((sg) => {
        const result = removeStepFromGroups(sg, stepCid);
        if (result == null) {
          return sg;
        }
        if (result.removedDocumentId) {
          DocumentActions.deleteDocument(result.removedDocumentId);
        }
        if (result.nextStepCid != null) {
          onChangeSelection(result.nextStepCid);
        }
        return result.newStepGroups;
      });
    },
    [onChangeSelection, onChangeSteps]
  );

  const saveDeps: StepSaveDeps = useMemo(
    () => ({powSaveImmediately, legacySaveImmediately, onChangeSteps, onChangeSelection}),
    [powSaveImmediately, legacySaveImmediately, onChangeSteps, onChangeSelection]
  );

  const addStep = useCallback(
    (stepGroupIndex: number, stepIndex: number, stepName?: string, fromId?: string) =>
      performAddStep(stepGroupIndex, stepIndex, saveDeps, stepName, fromId),
    [saveDeps]
  );

  const copyStep = useCallback(
    (stepGroupIndex: number, stepIndex: number) =>
      performCopyStep(stepGroupIndex, stepIndex, stepGroups, saveDeps),
    [saveDeps, stepGroups]
  );

  /**
   Method to delete the step after providing a confirmation modal to the user
   */
  const deleteStepWithConfirmation = useCallback(
    async (stepCid: string) => {
      const step = findStep(stepGroups, stepCid);

      if (!step) {
        // Step not found, possibly already deleted
        return;
      }

      const documentId = step.documentId;
      assert(documentId != null, 'Unexpectedly missing document ID');
      const document = DocumentStore.getDocument(documentId);
      assert(document != null, 'Unexpectedly missing document');

      if (DocumentUtil.isDocumentEmpty(document) || (await runConfirmDeleteModal({typeName: 'step'}))) {
        deleteStep(stepCid);
      }
    },
    [deleteStep, stepGroups]
  );

  return {
    moveStep,
    moveStepArbitrarily,
    addStep,
    copyStep,
    deleteStepWithConfirmation,
    isStepLimitReached,
  };
}
